import React, { useState } from 'react';
import AppHeader from '../../../components/AppHeader.tsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCalendar,
  faCheckCircle,
  faClock,
  faCloud,
  faDatabase,
  faDownload,
  faExclamationTriangle,
  faFile,
  faFileCsv,
  faFileExcel,
  faFilePdf,
  faFilter,
  faMailBulk,
} from '@fortawesome/free-solid-svg-icons';

const ExportDataPage: React.FC = () => {
  const [selectedDataType, setSelectedDataType] = useState('reservations');
  const [selectedFormat, setSelectedFormat] = useState('excel');
  const [dateRange, setDateRange] = useState({ from: '', to: '' });
  const [exportHistory, setExportHistory] = useState([
    {
      id: '1',
      type: 'Rezervace',
      format: 'Excel',
      dateRange: '01.11.2024 - 30.11.2024',
      createdAt: '2024-11-15T10:30:00Z',
      status: 'completed',
      size: '2.3 MB',
      downloadUrl: '#',
    },
    {
      id: '2',
      type: 'Zákazníci',
      format: 'CSV',
      dateRange: '01.01.2024 - 15.11.2024',
      createdAt: '2024-11-14T14:20:00Z',
      status: 'processing',
      size: '1.8 MB',
      downloadUrl: null,
    },
    {
      id: '3',
      type: 'Finanční data',
      format: 'PDF',
      dateRange: '01.10.2024 - 31.10.2024',
      createdAt: '2024-11-13T09:15:00Z',
      status: 'completed',
      size: '856 KB',
      downloadUrl: '#',
    },
  ]);

  const breadcrumb = [{ label: 'Reporty' }, { label: 'Export dat' }];

  const dataTypes = [
    {
      id: 'reservations',
      name: 'Rezervace',
      description: 'Kompletní data o rezervacích včetně zákazníků a služeb',
      icon: faCalendar,
      fields: ['Datum', 'Čas', 'Zákazník', 'Služba', 'Zaměstnanec', 'Cena', 'Stav'],
    },
    {
      id: 'customers',
      name: 'Zákazníci',
      description: 'Seznam všech zákazníků s kontaktními údaji a historií',
      icon: faDatabase,
      fields: ['Jméno', 'Email', 'Telefon', 'Adresa', 'Počet návštěv', 'Celkem utraceno'],
    },
    {
      id: 'financial',
      name: 'Finanční data',
      description: 'Příjmy, výdaje a finanční přehledy',
      icon: faFile,
      fields: ['Datum', 'Typ transakce', 'Částka', 'Popis', 'Kategorie', 'Metoda platby'],
    },
    {
      id: 'services',
      name: 'Služby',
      description: 'Katalog služeb s cenami a statistikami',
      icon: faFile,
      fields: ['Název služby', 'Kategorie', 'Cena', 'Doba trvání', 'Popis', 'Statistiky'],
    },
    {
      id: 'staff',
      name: 'Zaměstnanci',
      description: 'Data o zaměstnancích a jejich výkonnosti',
      icon: faDatabase,
      fields: ['Jméno', 'Pozice', 'Kontakt', 'Pracovní doby', 'Výkonnost', 'Mzdy'],
    },
  ];

  const exportFormats = [
    {
      id: 'excel',
      name: 'Excel (.xlsx)',
      description: 'Nejlepší pro analýzu dat',
      icon: faFileExcel,
      color: 'text-green-600',
    },
    {
      id: 'csv',
      name: 'CSV (.csv)',
      description: 'Univerzální formát pro import',
      icon: faFileCsv,
      color: 'text-blue-600',
    },
    {
      id: 'pdf',
      name: 'PDF (.pdf)',
      description: 'Vhodný pro tisk a sdílení',
      icon: faFilePdf,
      color: 'text-red-600',
    },
  ];

  const selectedDataTypeInfo = dataTypes.find(dt => dt.id === selectedDataType);
  const selectedFormatInfo = exportFormats.find(ef => ef.id === selectedFormat);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return faCheckCircle;
      case 'processing':
        return faClock;
      case 'failed':
        return faExclamationTriangle;
      default:
        return faClock;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'text-green-600';
      case 'processing':
        return 'text-yellow-600';
      case 'failed':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'completed':
        return 'Dokončeno';
      case 'processing':
        return 'Zpracovává se';
      case 'failed':
        return 'Chyba';
      default:
        return 'Neznámý';
    }
  };

  const handleExport = () => {
    console.log('Exporting data:', {
      dataType: selectedDataType,
      format: selectedFormat,
      dateRange,
    });
    // Add new export to history
    const newExport = {
      id: Date.now().toString(),
      type: selectedDataTypeInfo?.name || '',
      format: selectedFormatInfo?.name || '',
      dateRange: `${dateRange.from} - ${dateRange.to}`,
      createdAt: new Date().toISOString(),
      status: 'processing',
      size: '0 MB',
      downloadUrl: null,
    };
    setExportHistory(prev => [newExport, ...prev]);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('cs-CZ');
  };

  return (
    <div className="h-full flex flex-col">
      <AppHeader title="Export dat" breadcrumb={breadcrumb} />

      <div className="flex-1 overflow-y-auto bg-gray-50">
        <div className="p-6 space-y-6">
          {/* Header */}
          <section className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-semibold text-gray-900">Export dat</h1>
                <p className="text-gray-600 mt-1">Exportujte data z aplikace v různých formátech</p>
              </div>
              <div className="flex items-center space-x-3">
                <button className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors">
                  <FontAwesomeIcon icon={faDownload} className="mr-2" />
                  Exportovat
                </button>
              </div>
            </div>
          </section>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Export Configuration */}
            <section className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Konfigurace exportu</h3>

              {/* Data Type Selection */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-3">Typ dat</label>
                <div className="space-y-3">
                  {dataTypes.map(type => (
                    <button
                      key={type.id}
                      onClick={() => setSelectedDataType(type.id)}
                      className={`w-full p-4 rounded-lg border-2 transition-colors text-left ${
                        selectedDataType === type.id ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        <FontAwesomeIcon icon={type.icon} className="w-5 h-5 text-gray-600" />
                        <div>
                          <h4 className="font-medium text-gray-900">{type.name}</h4>
                          <p className="text-sm text-gray-600">{type.description}</p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Export Format Selection */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-3">Formát exportu</label>
                <div className="space-y-3">
                  {exportFormats.map(format => (
                    <button
                      key={format.id}
                      onClick={() => setSelectedFormat(format.id)}
                      className={`w-full p-4 rounded-lg border-2 transition-colors text-left ${
                        selectedFormat === format.id ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        <FontAwesomeIcon icon={format.icon} className={`w-5 h-5 ${format.color}`} />
                        <div>
                          <h4 className="font-medium text-gray-900">{format.name}</h4>
                          <p className="text-sm text-gray-600">{format.description}</p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Date Range Selection */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-3">Časové rozmezí</label>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Od</label>
                    <input
                      type="date"
                      value={dateRange.from}
                      onChange={e => setDateRange(prev => ({ ...prev, from: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Do</label>
                    <input
                      type="date"
                      value={dateRange.to}
                      onChange={e => setDateRange(prev => ({ ...prev, to: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
                    />
                  </div>
                </div>
              </div>

              {/* Export Button */}
              <button
                onClick={handleExport}
                disabled={!dateRange.from || !dateRange.to}
                className="w-full bg-green-600 text-white px-4 py-3 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <FontAwesomeIcon icon={faDownload} className="mr-2" />
                Exportovat {selectedDataTypeInfo?.name} ({selectedFormatInfo?.name})
              </button>
            </section>

            {/* Export Preview */}
            <section className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Náhled exportu</h3>

              <div className="space-y-4">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-medium text-gray-900 mb-2">Vybraná konfigurace</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Typ dat:</span>
                      <span className="text-gray-900">{selectedDataTypeInfo?.name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Formát:</span>
                      <span className="text-gray-900">{selectedFormatInfo?.name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Období:</span>
                      <span className="text-gray-900">{dateRange.from && dateRange.to ? `${dateRange.from} - ${dateRange.to}` : 'Nevybráno'}</span>
                    </div>
                  </div>
                </div>

                <div className="p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-medium text-gray-900 mb-2">Obsahované sloupce</h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedDataTypeInfo?.fields.map((field, index) => (
                      <span key={index} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                        {field}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                  <div className="flex items-center">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="w-5 h-5 text-yellow-600 mr-2" />
                    <div>
                      <h4 className="font-medium text-yellow-800">Důležité upozornění</h4>
                      <p className="text-sm text-yellow-700 mt-1">
                        Export může trvat několik minut v závislosti na množství dat. Po dokončení obdržíte e-mail s odkazem ke stažení.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </div>

          {/* Export History */}
          <section className="bg-white rounded-lg shadow-sm">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Historie exportů</h3>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {exportHistory.map(exportItem => (
                  <div key={exportItem.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                        <FontAwesomeIcon icon={faDownload} className="w-6 h-6 text-gray-600" />
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900">
                          {exportItem.type} ({exportItem.format})
                        </h4>
                        <p className="text-sm text-gray-600">{exportItem.dateRange}</p>
                        <p className="text-xs text-gray-500">Vytvořeno: {formatDate(exportItem.createdAt)}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-4">
                      <div className="text-right">
                        <div className="flex items-center space-x-2">
                          <FontAwesomeIcon icon={getStatusIcon(exportItem.status)} className={`w-4 h-4 ${getStatusColor(exportItem.status)}`} />
                          <span className={`text-sm font-medium ${getStatusColor(exportItem.status)}`}>{getStatusText(exportItem.status)}</span>
                        </div>
                        <p className="text-xs text-gray-500">{exportItem.size}</p>
                      </div>
                      {exportItem.status === 'completed' && exportItem.downloadUrl && (
                        <button className="bg-green-600 text-white px-3 py-2 rounded-lg hover:bg-green-700 transition-colors">
                          <FontAwesomeIcon icon={faDownload} className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Quick Actions */}
          <section className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Rychlé akce</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <button className="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-green-500 hover:bg-green-50 transition-colors">
                <FontAwesomeIcon icon={faCloud} className="w-6 h-6 text-gray-400 mr-3" />
                <span className="text-gray-600">Automatické zálohy</span>
              </button>
              <button className="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors">
                <FontAwesomeIcon icon={faMailBulk} className="w-6 h-6 text-gray-400 mr-3" />
                <span className="text-gray-600">E-mail reporty</span>
              </button>
              <button className="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-purple-500 hover:bg-purple-50 transition-colors">
                <FontAwesomeIcon icon={faDatabase} className="w-6 h-6 text-gray-400 mr-3" />
                <span className="text-gray-600">API přístup</span>
              </button>
              <button className="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-orange-500 hover:bg-orange-50 transition-colors">
                <FontAwesomeIcon icon={faFilter} className="w-6 h-6 text-gray-400 mr-3" />
                <span className="text-gray-600">Pokročilé filtry</span>
              </button>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default ExportDataPage;
